import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import EditBookingModal from "../../components/admin/EditBookingModal";
import ManualBookingModal from "../../components/admin/ManualBookingModal";
import { toast } from "sonner";
import { usePublicLocations } from "../../hooks/usePublicLocations";
import { useSafeFetch } from "../../lib/useSafeFetch";
import { Search, SlidersHorizontal, Plus } from "lucide-react";
import { isoToEU } from "../../lib/format";

import {
  DEFAULT_SOURCES, SOURCE_LABEL, STATUS_OPTIONS, REQUIRES_PAYMENT_METHOD,
} from "../../components/admin/bookings/constants";
import AdvancedSearchPanel from "../../components/admin/bookings/AdvancedSearchPanel";
import BookingsTable from "../../components/admin/bookings/BookingsTable";
import {
  PaymentMethodDialog, DeleteBookingDialog,
} from "../../components/admin/bookings/BookingDialogs";

const EMPTY_ADVANCED = {
  booking_id: "", customer_name: "", vehicle_plate: "",
  pickup_location: "all", return_location: "all",
  pickup_date_from: "", pickup_date_to: "",
  date_created_from: "", date_created_to: "",
};

export default function AdminBookings() {
  const [filter, setFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [methodPicker, setMethodPicker] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [deletingBooking, setDeletingBooking] = useState(null);
  const [reviewingModification, setReviewingModification] = useState(null);
  const [sources, setSources] = useState(DEFAULT_SOURCES);
  const [advanced, setAdvanced] = useState(EMPTY_ADVANCED);
  const locations = usePublicLocations();

  const {
    data: bookings, loading: fetchLoading, refetch: load, setData: setBookings,
  } = useSafeFetch("/bookings/all", { defaultValue: [], asList: true });
  const [advLoading, setAdvLoading] = useState(false);
  const loading = fetchLoading || advLoading;

  useEffect(() => {
    api.get("/settings/public")
      .then((r) => {
        const s = r?.data?.booking_sources;
        if (Array.isArray(s) && s.length) setSources(s);
      })
      .catch(() => { /* keep defaults */ });
  }, []);

  const runAdvancedSearch = async () => {
    setAdvLoading(true);
    setError("");
    try {
      const params = {};
      Object.entries(advanced).forEach(([k, v]) => {
        if (v && v !== "all") params[k] = v;
      });
      const { data } = await api.get("/admin/bookings", { params });
      setBookings(Array.isArray(data?.results) ? data.results : []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAdvLoading(false);
    }
  };

  const resetAdvanced = () => {
    setAdvanced(EMPTY_ADVANCED);
    load();
  };

  const patchStatus = async (id, status, payment_method = null) => {
    try {
      const payload = payment_method ? { status, payment_method } : { status };
      const { data } = await api.patch(`/bookings/${id}/status`, payload);
      setBookings((prev) => prev.map((b) => b.id === id ? { ...b, ...data } : b));
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const handleStatusChange = (booking, newStatus) => {
    if (newStatus === booking.status) return;
    if (REQUIRES_PAYMENT_METHOD.has(newStatus)) {
      setMethodPicker({
        bookingId: booking.id,
        status: newStatus,
        bookingLabel: `${booking.full_name} · ${booking.vehicle_snapshot?.name}`,
      });
    } else {
      patchStatus(booking.id, newStatus);
    }
  };

  const confirmMethod = async (method) => {
    if (!methodPicker) return;
    await patchStatus(methodPicker.bookingId, methodPicker.status, method);
    setMethodPicker(null);
  };

  const filtered = (Array.isArray(bookings) ? bookings : []).filter((b) => {
    if (filter !== "all" && b.status !== filter) return false;
    if (sourceFilter !== "all" && (b.source || "internet") !== sourceFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!(b.full_name?.toLowerCase().includes(s) ||
            b.user_email?.toLowerCase().includes(s) ||
            b.vehicle_snapshot?.name?.toLowerCase().includes(s) ||
            b.id.toLowerCase().includes(s))) return false;
    }
    return true;
  });

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Reservations</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Bookings <em className="italic">log</em>.
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">
            {filtered.length} of {bookings.length} bookings
          </p>
        </div>
        <button
          type="button"
          onClick={() => setNewBookingOpen(true)}
          className="bg-black text-white dark:bg-white dark:text-black px-4 py-2 text-sm inline-flex items-center gap-2 hover:opacity-90 transition-opacity rounded"
          data-testid="new-reservation-btn"
        >
          <Plus size={14} /> New reservation
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <div className="flex gap-1 bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 p-1 rounded">
          {[{ id: "all", label: "All" }, ...STATUS_OPTIONS.map((s) => ({ id: s.value, label: s.label }))].map((c) => (
            <button
              key={c.id}
              onClick={() => setFilter(c.id)}
              className={`px-3 py-1.5 text-xs rounded transition-colors ${
                filter === c.id
                  ? "bg-black text-white dark:bg-white dark:text-black"
                  : "text-neutral-600 dark:text-neutral-400 hover:bg-black/5 dark:hover:bg-white/5"
              }`}
              data-testid={`filter-${c.id}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
            <span className="tracking-widest uppercase">Source</span>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 px-2 py-1.5 rounded text-xs outline-none"
              data-testid="source-filter"
            >
              <option value="all">All sources</option>
              {(Array.isArray(sources) ? sources : DEFAULT_SOURCES).map((s) => (
                <option key={s} value={s}>{SOURCE_LABEL(s)}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={() => setAdvancedOpen((o) => !o)}
            className={`px-3 py-1.5 text-xs rounded border inline-flex items-center gap-1 transition-colors ${
              advancedOpen
                ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white"
                : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white"
            }`}
            data-testid="advanced-toggle"
          >
            <SlidersHorizontal size={12} /> Advanced
          </button>
          <div className="flex items-center gap-2 border border-black/10 dark:border-white/10 bg-white dark:bg-[#1F2937] px-3 py-1.5 rounded">
            <Search size={14} className="text-neutral-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, vehicle, ID"
              className="bg-transparent outline-none text-sm w-56"
              data-testid="bookings-search"
            />
          </div>
        </div>
      </div>

      {advancedOpen && (
        <AdvancedSearchPanel
          advanced={advanced}
          setAdvanced={setAdvanced}
          locations={locations}
          onApply={runAdvancedSearch}
          onReset={resetAdvanced}
        />
      )}

      {error && (
        <p className="text-red-600 dark:text-rose-400 text-sm mb-4" data-testid="bookings-error">{error}</p>
      )}

      <BookingsTable
        bookings={filtered}
        loading={loading}
        onStatusChange={handleStatusChange}
        onEdit={setEditingBooking}
        onDelete={setDeletingBooking}
        onReviewModification={setReviewingModification}
      />

      {reviewingModification && (
        <ModificationReviewModal
          booking={reviewingModification}
          onClose={() => setReviewingModification(null)}
          onDone={() => { setReviewingModification(null); load(); }}
        />
      )}

      {methodPicker && (
        <PaymentMethodDialog
          label={methodPicker.bookingLabel}
          targetStatus={methodPicker.status}
          onCancel={() => setMethodPicker(null)}
          onConfirm={confirmMethod}
        />
      )}

      <ManualBookingModal
        open={newBookingOpen}
        onClose={() => setNewBookingOpen(false)}
        onCreated={() => load()}
      />

      <EditBookingModal
        open={!!editingBooking}
        booking={editingBooking}
        onClose={() => setEditingBooking(null)}
        onSaved={() => load()}
      />

      {deletingBooking && (
        <DeleteBookingDialog
          booking={deletingBooking}
          onCancel={() => setDeletingBooking(null)}
          onConfirmed={async () => {
            try {
              await api.delete(`/admin/bookings/${deletingBooking.id}`);
              toast.success("Booking deleted");
              setBookings((prev) => prev.filter((x) => x.id !== deletingBooking.id));
              setDeletingBooking(null);
            } catch (err) {
              toast.error("Delete failed", {
                description: formatApiError(err?.response?.data?.detail) || err.message,
              });
            }
          }}
        />
      )}
    </div>
  );
}

// ── Modification approval modal (v3.9.6) ────────────────────────────────────
function ModificationReviewModal({ booking, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const pending = booking.pending_modification;
  const requested = pending?.requested || {};
  const previous  = pending?.previous  || {};
  // v3.9.8 — Prefer authoritative server-side recalculation.
  const newTotal  = pending?.new_total  != null ? Number(pending.new_total)  : null;
  const priceDiff = pending?.price_diff != null ? Number(pending.price_diff) : null;
  const swap      = pending?.swap_vehicle;
  const fmt = (n) => `€${Number(n || 0).toFixed(2)}`;

  const decide = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${booking.id}/modification/${action}`);
      toast.success(action === "approve" ? "Modification approved" : "Modification rejected");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose} data-testid="modification-review-modal">
      <div className="bg-white dark:bg-[#1F2937] text-[#0A0A0A] dark:text-[#F9FAFB] max-w-2xl w-full rounded border border-black/10 dark:border-white/10 p-6 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">Customer request</p>
            <h2 className="font-display text-2xl mt-1">Review modification</h2>
            <p className="text-xs text-neutral-500 mt-1 font-mono">{booking.id.slice(0, 8).toUpperCase()} · {booking.full_name}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="p-3 rounded border border-black/10 dark:border-white/10">
            <p className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Current</p>
            <ul className="space-y-1 tabular-nums">
              <li>Pickup: {isoToEU(previous.pickup_date)} {previous.pickup_time || ""}</li>
              <li>Return: {isoToEU(previous.return_date)} {previous.return_time || ""}</li>
              {previous.pickup_location && <li>Pickup loc: {previous.pickup_location}</li>}
              {previous.return_location && <li>Return loc: {previous.return_location}</li>}
              {previous.vehicle_name && <li>Vehicle: {previous.vehicle_name}</li>}
              <li>Addons: {(previous.addons || []).join(", ") || "—"}</li>
              <li>Total: {fmt(previous.total)}</li>
            </ul>
          </div>
          <div className="p-3 rounded border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700" data-testid="modification-diff">
            <p className="text-[10px] uppercase tracking-widest text-amber-800 dark:text-amber-300 mb-2">Requested</p>
            <ul className="space-y-1 tabular-nums">
              {"pickup_date" in requested && <li>Pickup: {isoToEU(requested.pickup_date)}</li>}
              {"pickup_time" in requested && <li>Pickup time: {requested.pickup_time}</li>}
              {"return_date" in requested && <li>Return: {isoToEU(requested.return_date)}</li>}
              {"return_time" in requested && <li>Return time: {requested.return_time}</li>}
              {"pickup_location" in requested && <li>Pickup loc: {requested.pickup_location}</li>}
              {"return_location" in requested && <li>Return loc: {requested.return_location}</li>}
              {swap && <li>Vehicle: {swap.brand ? `${swap.brand} ` : ""}{swap.name}</li>}
              {"addons" in requested && <li>Addons: {(requested.addons || []).join(", ") || "—"}</li>}
              {Object.keys(requested).length === 0 && <li className="italic text-neutral-500">(No changes captured)</li>}
            </ul>
            {pending?.reason && (
              <p className="text-xs italic mt-2 text-amber-800 dark:text-amber-200">"{pending.reason}"</p>
            )}
          </div>
        </div>

        {(priceDiff !== null || newTotal !== null) && (
          <div className="mt-4 flex flex-wrap items-baseline gap-x-6 gap-y-1 p-3 rounded border border-black/10 dark:border-white/10 bg-neutral-50 dark:bg-[#111827]" data-testid="modification-price-impact">
            {priceDiff !== null && (
              <p>
                <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-2">Price difference:</span>
                <strong className={`text-lg ${priceDiff > 0 ? "text-rose-700 dark:text-rose-400" : priceDiff < 0 ? "text-emerald-700 dark:text-emerald-400" : ""}`}>
                  {priceDiff > 0 ? "+" : ""}{fmt(priceDiff)}
                </strong>
              </p>
            )}
            {newTotal !== null && (
              <p>
                <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-2">New total:</span>
                <strong className="text-lg">{fmt(newTotal)}</strong>
                <span className="ml-2 text-xs text-neutral-500 line-through">was {fmt(previous.total)}</span>
              </p>
            )}
          </div>
        )}

        <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-4">
          Approving applies both the change and the new total. Prices are computed from active seasonal rates; verify with the customer if you're unsure.
        </p>

        <div className="flex justify-end gap-3 mt-6">
          <button type="button" onClick={onClose} className="border border-black/15 dark:border-white/15 px-4 py-2 text-sm rounded hover:border-black dark:hover:border-white" data-testid="mod-cancel">Cancel</button>
          <button type="button" disabled={busy} onClick={() => decide("reject")}
            className="border border-rose-300 text-rose-700 dark:border-rose-700 dark:text-rose-300 px-4 py-2 text-sm rounded hover:bg-rose-50 dark:hover:bg-rose-900/30 disabled:opacity-40"
            data-testid="mod-reject">Reject modification</button>
          <button type="button" disabled={busy} onClick={() => decide("approve")}
            className="bg-black text-white dark:bg-white dark:text-black px-4 py-2 text-sm rounded hover:opacity-90 disabled:opacity-40"
            data-testid="mod-approve">Approve modification</button>
        </div>
      </div>
    </div>
  );
}
